/*
 * Copyright (c) 2020. American Express Company
 */

// theirs
import * as _ from 'lodash';
import React from 'react';
import { isSafari } from 'react-device-detect';

// ours
import { ChronoUnit, LocalDate } from '@js-joda/core';
import { CashFlowModel } from 'src/types/cash-flow.model';
import { FREECASH_VISIBLE } from 'src/utils/constants';

// styles
import colours from 'src/styles/variables.module.scss';

export default class UtilsService {
    // List of URL parameters that trigger temporary user creation. Any one or combination of these will work.
    // income, expenses, investments, debt fields are deprecated aliases
    static readonly LOAD_USER_PARAM = [
        'guid',
        'age',
        'dob',
        'monthly_income',
        'annual_income',
        'income',
        'monthly_home_expense',
        'annual_home_expense',
        'expenses',
        'home_savings_investment',
        'investments',
        'debt',
        'debts',
        'jwt'
    ];

    // data viz colours
    public static readonly AMEX_COLOR: string[] = [
        colours.aqua400,
        colours.aqua500,
        colours.aqua600,
        colours.brightBlue400,
        colours.brightBlue500,
        colours.brightBlue600,
        colours.deepBlue400,
        colours.deepBlue500,
        colours.deepBlue600,
        colours.green400,
        colours.green500,
        colours.green600,
        colours.magenta400,
        colours.magenta500,
        colours.magenta600,
        colours.orange400,
        colours.orange500,
        colours.orange600
    ];

    // Hashmap to store a pair of string and its corresponding color
    // thus, for any given input string, there is the unique deterministic color
    private static readonly stringColorMap: Map<string, string> = new Map<string, string>();
    private static readonly totalNums: number = UtilsService.AMEX_COLOR.length;

    /**
     * Returns a list of hex colors of a given string list
     * @param strList
     */
    public static getColorListForStringList(strList: string[]): string[] {
        return strList.map((i) => this.getAmexColorForString(i));
    }

    /**
     * Returns the color of a given string. Based on the hashcode of the string, find the color from AMEX_COLOR
     * @param string
     */
    public static getAmexColorForString(string: string): string {
        if (!this.stringColorMap.has(string)) {
            const hashCode = this.getHashCodeForString(string, this.totalNums - 1) % this.totalNums;
            this.stringColorMap.set(string, this.AMEX_COLOR.at(hashCode));
        }
        return this.stringColorMap.get(string);
    }

    /**
     * Returns a hash code of a given string
     * @param string
     * @param hash , the seed (so small string doesn't always end in zeros)
     */
    private static getHashCodeForString(string: string, hash: number = 0x811c9dc5): number {
        for (let i = 0; i < string.length; i++) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }
        return hash;
    }

    public static getColorForString(string: string): string {
        return `#${Math.abs(this.getHashCodeForString(string)).toString(16).padStart(6, '0').substring(0, 6)}`;
    }

    public static roundFloatNumber(num: number | string, decimalPlaces: number): number {
        const numToFixedDp = Number(num).toFixed(decimalPlaces);
        return Number(numToFixedDp);
    }

    public static capitalizeFirstLetter(str: string): string {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    public static capitalizeFirstLetterOfEachWord(str: string): string {
        return str.toLowerCase().replace(/(^|\s)\S/g, (l) => l.toUpperCase());
    }

    public static isBase64(str: string): boolean {
        return str.includes('data:image');
    }

    public static truncate(str: string, n: number): string {
        return str.length > n ? str.substr(0, n - 1) + '...' : str;
    }

    public static getAge(birthday: LocalDate): number {
        return birthday.until(LocalDate.now(), ChronoUnit.YEARS);
    }

    public static getNetWorth(cashFlow: CashFlowModel): number {
        if (cashFlow) {
            if (FREECASH_VISIBLE) {
                return Math.round(
                    Number(cashFlow.freeCash) + Number(cashFlow.investment) - Math.abs(Number(cashFlow.debt))
                );
            } else {
                return Math.round(Number(cashFlow.investment) - Math.abs(Number(cashFlow.debt)));
            }
        }

        return 0;
    }

    public static formatList(list: string[], joiner: string = 'and'): string {
        switch (true) {
            case list.length === 0:
                return '';

            case list.length === 1:
                return list[0];

            case list.length === 2:
                return `${list[0]} ${joiner} ${list[1]}`;

            case list.length > 2:
                let result: string = '';
                for (let i = 0; i < list.length - 1; i++) {
                    result += list[i] + ', ';
                }
                result += `${joiner} ` + list[list.length - 1];
                return result;

            default:
                console.error('invalid input');
                return '';
        }
    }

    public static formatListBold(list: string[], joiner: string = 'and'): JSX.Element {
        switch (true) {
            case list.length === 0:
                return null;

            case list.length === 1:
                return <b>{list[0]}</b>;

            case list.length === 2:
                return (
                    <span>
                        <b>{list[0]}</b> {joiner} <b>{list[1]}</b>
                    </span>
                );

            case list.length > 2:
                const inner: JSX.Element[] = list.splice(0, list.length - 1).map((s: string, index: number) => (
                    <React.Fragment key={`${s}-${index}`}>
                        <b>{s}</b>,{' '}
                    </React.Fragment>
                ));

                return (
                    <span>
                        {inner.map((frag) => frag)} {joiner} <b>{list[list.length - 1]}</b>
                    </span>
                );

            default:
                console.error('invalid input');
                return null;
        }
    }

    public static getDragCursorOffset(el: Element): number {
        const baseOffset: number = el.clientWidth / 2;
        return isSafari ? baseOffset : baseOffset * window.devicePixelRatio;
    }

    public static partiallyCLoneObject<S, T>(source: S, target: T): T {
        return _.pick(source, Object.keys(target)) as T;
    }

    public static convertInterest(interest: number): number {
        if (typeof interest === 'number') {
            return this.roundFloatNumber(interest * 100, 0);
        } else {
            return null;
        }
    }

    /** Reorders an array when an element has been dragged and dropped to a new destination */
    public static dragReorder(activeIndex: number, overIndex: number, items: any[]): any[] {
        const newItems = [...items];
        const [removed] = newItems.splice(activeIndex, 1);
        newItems.splice(overIndex, 0, removed);
        return newItems;
    }
}
